import React from 'react';
import { useTranslation } from 'react-i18next';
import type { UseAdminQuotesReturn } from '../../hooks/useAdminQuotes';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Button } from '../ui/button';
import { StatusBadge } from '../shared/StatusBadge';
import { Card, CardContent } from '../ui/card';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { Eye, Search, MessageSquare } from 'lucide-react';

export const AdminQuotesList: React.FC<UseAdminQuotesReturn> = ({
  quotes,
  loading,
  statusFilter,
  setStatusFilter,
  searchQuery,
  setSearchQuery,
  handleSearch,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  paginatedItems,
  totalPages,
  navigateToQuote,
}) => {
  const { t } = useTranslation();

  const isValidTimestamp = (value?: string | null): value is string =>
    typeof value === 'string' && value.length > 0 && !Number.isNaN(Date.parse(value));

  const relativeToNow = (value?: string | null): string | null =>
    isValidTimestamp(value) ? formatDistanceToNow(new Date(value), { addSuffix: true }) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-3xl tracking-tight">{t('admin.quotes.title')}</h2>
        
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <input
                type="text"
                placeholder={t('admin.quotes.search_placeholder')}
                className="pl-9 h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button type="submit" variant="secondary">
              {t('common.filter')}
            </Button>
          </form>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
          >
            <option value="">{t('admin.filter.all_statuses')}</option>
            <option value="pending">{t('admin.quotes.status.pending')}</option>
            <option value="quoted">{t('admin.quotes.status.quoted')}</option>
            <option value="awaiting_contact">{t('admin.quotes.status.awaiting_contact')}</option>
            <option value="contacted">{t('admin.quotes.status.contacted')}</option>
            <option value="rejected">{t('admin.quotes.status.rejected')}</option>
            <option value="canceled">{t('admin.quotes.status.canceled')}</option>
          </select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.quotes.columns.tracking')}</TableHead>
                <TableHead>{t('admin.quotes.columns.client')}</TableHead>
                <TableHead>{t('admin.quotes.columns.route')}</TableHead>
                <TableHead>{t('admin.quotes.columns.status')}</TableHead>
                <TableHead>{t('admin.quotes.columns.responses')}</TableHead>
                <TableHead>{t('admin.quotes.columns.contacted')}</TableHead>
                <TableHead>{t('admin.quotes.columns.date')}</TableHead>
                <TableHead className="text-right">{t('admin.quotes.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10">
                    {t('admin.quotes.loading')}
                  </TableCell>
                </TableRow>
              ) : quotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                    {t('admin.quotes.noQuotes')}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell className="font-medium">{quote.tracking_number}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{quote.contact_name}</span>
                        <span className="text-xs text-muted-foreground">{quote.contact_email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm">
                        <span className="font-medium">{quote.origin_city}, {quote.origin_country}</span>
                        <span className="mx-2 text-muted-foreground">→</span>
                        <span className="font-medium">{quote.destination_city}, {quote.destination_country}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={quote.status}
                        tPrefix="admin.quotes.status"
                        className="shrink-0"
                      />
                    </TableCell>
                    <TableCell>
                      {(quote as any).response_count || 0}
                    </TableCell>
                    <TableCell>
                      {(quote as any).companies_contacted || 0}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {relativeToNow(quote.created_at) ?? ''}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => navigateToQuote(quote.id, true)}
                          title={t('admin.quotes.view_responses')}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => navigateToQuote(quote.id)}
                          title={t('admin.quotes.view_details')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('common.items_per_page')}:
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('common.page_info', { current: currentPage, total: totalPages || 1 })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
